import { useState, useEffect, useMemo, type ChangeEvent, type MouseEvent } from "react";
import { useAppDispatch } from "../../store/hooks";
import {
    Box,
    Button,
    Dialog,
    Paper,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress,
    Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { FolderOutlined, DragIndicator } from "@mui/icons-material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";
import useAccountFiles from "../../hooks/queries/useAccountFiles";
import useAddAccountFile from "../../hooks/useAddAccountFile";
import useDeleteAccountFile from "../../hooks/useDeleteAccountFile";
import useUpdateAccountFile from "../../hooks/useUpdateAccountFile";
import { setSnackAction } from "../../store/slices/snackSlice";
import type { AccountFile, AccountFileFolder } from "../../services/documentService";

const useStyles = makeStyles()((theme) => ({
    card: {
        margin: "0 auto",
        padding: theme.spacing(6),
        width: "100%",
        [theme.breakpoints.up("md")]: { width: "75%" },
        [theme.breakpoints.up("xl")]: { width: "50%" },
    },
    attachNewFileInput: { display: "none" },
    attachFileButton: { marginLeft: theme.spacing(4) },
    padTop: { paddingTop: theme.spacing(2) },
    folder: {
        border: "1px solid #e0e0e0",
        borderRadius: 4,
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1, 2),
    },
    folderHeader: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        borderBottom: "1px solid #f0f0f0",
        marginBottom: theme.spacing(1),
    },
    folderTitle: { fontWeight: 700 },
    fileList: {
        minHeight: 40,
        padding: theme.spacing(0.5, 0),
        transition: "background-color 120ms ease",
    },
    fileListOver: { backgroundColor: "#f5faff" },
    fileListEmpty: {
        color: "#aaa",
        fontSize: 12,
        padding: theme.spacing(1),
        fontStyle: "italic",
    },
    fileRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(0.75, 1),
        borderRadius: 4,
        backgroundColor: "white",
        marginBottom: theme.spacing(0.5),
        border: "1px solid #eee",
    },
    fileRowDragging: {
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        backgroundColor: "#fafcff",
    },
    dragHandle: { color: "#aaa", cursor: "grab" },
    fileMeta: { color: "#888", fontSize: 12 },
    removeFileIcon: { cursor: "pointer" },
}));

const PERMANENT_FOLDERS = [
    "General Risk Assessments",
    "Fire Risk Assessments",
    "Manual and Handbook",
    "Miscellaneous",
];

const UPLOADABLE_FOLDERS = new Set(["Miscellaneous"]);

const fileTypeHS = 1;
const documentType = 0;

// Legacy URI shape: "<...> Safety/<folder>/<filename>". Recompute the URI when a
// file moves to a different folder so the back-end blob path matches the tree.
const reparentUri = (oldUri: string, newFolderTitle: string): string => {
    const prefix = oldUri.split("Safety")[0] + "Safety";
    const fileName = oldUri.split("/").pop();
    return `${prefix}/${newFolderTitle}/${fileName}`;
};

export interface DocumentsTableAccount {
    accountId?: number | string;
    accountName?: string;
}

interface DocumentsTableProps {
    account: DocumentsTableAccount;
}

const DocumentsTable = ({ account }: DocumentsTableProps) => {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();

    const accountId = account?.accountId;
    const { data, isLoading, isError, error } = useAccountFiles(
        accountId,
        fileTypeHS,
        documentType,
    );
    const addAccountFile = useAddAccountFile();
    const deleteAccountFile = useDeleteAccountFile();
    const updateAccountFile = useUpdateAccountFile();

    const [fileToRemove, setFileToRemove] = useState<AccountFile | null>(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);

    // Local optimistic copy of the tree so drags update the UI before the
    // server round-trips.
    const [folders, setFolders] = useState<AccountFileFolder[]>(() =>
        PERMANENT_FOLDERS.map((title) => ({ title, children: [] })),
    );

    useEffect(() => {
        if (!data) return;
        setFolders(
            PERMANENT_FOLDERS.map((title) => {
                const match = data.find((d) => d.title === title);
                return { title, children: match?.children ?? [] };
            }),
        );
    }, [data]);

    const folderIndexByTitle = useMemo(
        () => Object.fromEntries(folders.map((f, i) => [f.title, i] as const)),
        [folders],
    );

    const handleUploadAccountFile = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files?.[0] || accountId === undefined) return;
        Array.from(files).forEach((file) => {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("fileName", file.name);
            formData.append("accountId", String(accountId));
            formData.append("accountFileTypeId", String(fileTypeHS));

            addAccountFile.mutate(
                { formData, accountId, fileTypeHS, documentType },
                {
                    onError: (e) =>
                        dispatch(
                            setSnackAction(
                                e?.message || "There was an error adding the file",
                                "error",
                            ),
                        ),
                    onSuccess: () => dispatch(setSnackAction("File added", "success")),
                },
            );
        });
    };

    const openDeleteFilePopup = (e: MouseEvent, file: AccountFile) => {
        e.stopPropagation();
        setFileToRemove(file);
        setIsDeleteOpen(true);
    };

    const handleRemoveFile = () => {
        if (!fileToRemove) return;
        const formData = new FormData();
        formData.append("fileName", fileToRemove.uri);

        deleteAccountFile.mutate(
            { formData, documentType },
            {
                onError: (e) =>
                    dispatch(
                        setSnackAction(
                            e?.message || "There was an error removing the file",
                            "error",
                        ),
                    ),
                onSuccess: () => dispatch(setSnackAction("File removed", "success")),
            },
        );
        setIsDeleteOpen(false);
    };

    const handleDragEnd = (result: DropResult) => {
        const { source, destination } = result;
        if (!destination) return;
        if (source.droppableId === destination.droppableId && source.index === destination.index)
            return;

        const sourceIdx = folderIndexByTitle[source.droppableId];
        const destIdx = folderIndexByTitle[destination.droppableId];
        if (sourceIdx == null || destIdx == null) return;

        const next = folders.map((f) => ({ ...f, children: [...f.children] }));
        const [moved] = next[sourceIdx].children.splice(source.index, 1);
        next[destIdx].children.splice(destination.index, 0, moved);
        setFolders(next);

        if (source.droppableId === destination.droppableId) return;

        const newFileName = reparentUri(moved.uri, destination.droppableId);
        if (moved.uri === newFileName) return;

        const formData = new FormData();
        formData.append("fileName", moved.uri);
        formData.append("newFileName", newFileName);

        updateAccountFile.mutate(
            { formData, documentType },
            {
                onError: (e) => {
                    setFolders(folders);
                    dispatch(
                        setSnackAction(
                            e?.message || "There was an error moving the file",
                            "error",
                        ),
                    );
                },
                onSuccess: () => dispatch(setSnackAction("File moved", "success")),
            },
        );
    };

    if (isLoading)
        return (
            <Paper className={classes.card}>
                <Box display="flex" justifyContent="center" p={6}>
                    <CircularProgress size={48} />
                </Box>
            </Paper>
        );

    if (isError)
        return (
            <Paper className={classes.card}>
                <Box display="flex" justifyContent="center" p={6}>
                    <Typography>
                        {error?.message || "Could not fetch files for account"}
                    </Typography>
                </Box>
            </Paper>
        );

    return (
        <Paper className={classes.card}>
            <Box display="flex" justifyContent="space-between" alignItems="center" className={classes.padTop}>
                <Typography variant="h4" color="primary">
                    {account?.accountName}
                </Typography>
                <Button
                    className={classes.attachFileButton}
                    variant="outlined"
                    color="primary"
                    component="label"
                >
                    <input
                        type="file"
                        className={classes.attachNewFileInput}
                        onChange={handleUploadAccountFile}
                        multiple
                    />
                    Add document
                </Button>
            </Box>

            <Box height="70vh" p={2} mt={2} sx={{ overflow: "auto" }}>
                <DragDropContext onDragEnd={handleDragEnd}>
                    {folders.map((folder) => (
                        <Box key={folder.title} className={classes.folder}>
                            <Box className={classes.folderHeader}>
                                <FolderOutlined fontSize="small" />
                                <Typography className={classes.folderTitle}>{folder.title}</Typography>
                                <Typography className={classes.fileMeta}>
                                    {folder.children.length} file{folder.children.length === 1 ? "" : "s"}
                                </Typography>
                            </Box>
                            <Droppable droppableId={folder.title}>
                                {(provided, snapshot) => (
                                    <Box
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className={[
                                            classes.fileList,
                                            snapshot.isDraggingOver ? classes.fileListOver : "",
                                        ].join(" ")}
                                    >
                                        {folder.children.length === 0 && !snapshot.isDraggingOver && (
                                            <Typography className={classes.fileListEmpty}>
                                                {UPLOADABLE_FOLDERS.has(folder.title) ? "Drop files here" : "No documents"}
                                            </Typography>
                                        )}
                                        {folder.children.map((file, idx) => (
                                            <Draggable key={file.uri} draggableId={file.uri} index={idx}>
                                                {(dragProvided, dragSnapshot) => (
                                                    <Box
                                                        ref={dragProvided.innerRef}
                                                        {...dragProvided.draggableProps}
                                                        className={[
                                                            classes.fileRow,
                                                            dragSnapshot.isDragging ? classes.fileRowDragging : "",
                                                        ].join(" ")}
                                                    >
                                                        <Box {...dragProvided.dragHandleProps} className={classes.dragHandle}>
                                                            <DragIndicator fontSize="small" />
                                                        </Box>
                                                        <Box flexGrow={1}>
                                                            <Typography>{file.title}</Typography>
                                                            {file.subtitle && (
                                                                <Typography className={classes.fileMeta}>
                                                                    Date Added: {file.subtitle}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <DeleteOutlineOutlinedIcon
                                                            color="error"
                                                            className={classes.removeFileIcon}
                                                            onClick={(e) => openDeleteFilePopup(e, file)}
                                                        />
                                                    </Box>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </Box>
                                )}
                            </Droppable>
                        </Box>
                    ))}
                </DragDropContext>
            </Box>

            <Dialog
                open={isDeleteOpen}
                onClose={() => setIsDeleteOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Confirm Removal</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Please confirm that you wish to remove the file
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteOpen(false)}>Cancel</Button>
                    <Button onClick={handleRemoveFile} color="primary">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default DocumentsTable;
